import {defineSlotRecipe} from '@chakra-ui/react3'

export const searchOverlayRecipe = defineSlotRecipe({
    slots: [
        'backdrop',
        'positioner',
        'content',
        'body',
        'closeButton',
        'search',
        'searchInput',
        'contentContainer',
        'contentItem',
        'contentItemLabel',
        'result',
        'resultImage',
        'resultText',
        'contentLabel',
        'resultHighlight',
        'recentSearches',
        'clearRecentSearchLink',
        'viewAllLink',
        'form',
        'searchIcon',
        'searchEndElement',
        'noResultContainer'
    ],
    base: {
        backdrop: {
            bg: 'background.overlay.subtle',
            backdropFilter: 'blur(12px)',
            _open: {
                animationName: 'none',
                animationDuration: '0s'
            },
            _closed: {
                animationName: 'none',
                animationDuration: '0s'
            }
        },
        positioner: {
            // The desktop portal is mounted in the rendered header card, whose
            // containing block already has the card's exact bounds.
            position: {s: 'absolute'},
            width: {s: 'auto'},
            // At `base` the overlay hangs off the header's measured top edge, so a
            // scrolled-away message bar stops dropping the field a bar below the
            // button it replaces (STOK-17653). The var lives on the header root —
            // `base` needs a `portalContainer` inside it.
            height: {
                base: 'calc(100dvh - var(--header-viewport-offset-block, 0px))',
                s: 'auto'
            },
            insetBlockStart: {base: 'var(--header-viewport-offset-block, 0px)', s: 0},
            insetInlineStart: {s: 0},
            insetInlineEnd: {s: 0},
            alignItems: 'flex-start',
            justifyContent: 'center'
        },
        content: {
            padding: {base: 'none', s: '0'},
            width: 'full',
            minBlockSize: {base: '100%', s: 'auto'},
            maxBlockSize: {base: '100%', s: '90dvh'},
            maxWidth: {base: 'container-page-max-width', s: 'none'},
            backgroundColor: {base: 'primary.base.white', s: 'transparent'},
            _open: {
                animationName: 'none',
                animationDuration: '0s'
            },
            _closed: {
                animationName: 'none',
                animationDuration: '0s'
            },
            _before: {
                display: 'none'
            }
        },
        body: {
            flex: 0,
            width: {base: 'auto', s: 'full'},
            bg: {base: 'primary.base.white', s: 'transparent'},
            display: 'flex',
            flexDirection: 'column',
            maxBlockSize: {base: '100%', s: '90dvh'},
            overflow: {s: 'hidden'}
        },
        closeButton: {
            width: 'max-content',
            cursor: 'pointer',
            flexShrink: '0',
            textStyle: 'body.small',
            color: 'text.primary',
            // The invisible button keeps its flex space so the input's expanded
            // width already accounts for it before the fade-in starts.
            opacity: {base: 0, s: 1},
            visibility: {base: 'hidden', s: 'visible'},
            transitionProperty: {base: 'opacity, visibility', s: 'none'},
            transitionDuration: {base: 'fast', s: '0s'},
            transitionTimingFunction: 'ease-in-out',
            transitionDelay: '0ms',
            _motionReduce: {
                transitionDuration: 'fastest',
                transitionDelay: '0ms'
            }
        },
        form: {
            // The two terms the box below is made of, held as variables so the
            // padding, the input and the box each read one number — the
            // expansion then moves the input height alone.
            '--search-overlay-form-block-inset': '{spacing.spacing-16}',
            '--search-overlay-input-block-size': '2rem',
            pt: 'var(--search-overlay-form-block-inset)',
            pr: {base: 'spacing-20', s: 'spacing-32'},
            pb: 'var(--search-overlay-form-block-inset)',
            pl: {base: 'spacing-24', s: 'spacing-16'},
            border: {base: 'none', s: '1px solid'},
            borderColor: {s: 'border.subtle'},
            borderRadius: 'l',
            display: 'flex',
            flexShrink: 0,
            // The inset is kept above and below the input throughout the
            // expansion.
            blockSize: {
                s: 'calc(var(--search-overlay-input-block-size) + 2 * var(--search-overlay-form-block-inset))'
            },
            transitionProperty: 'block-size',
            transitionDuration: 'slower',
            transitionTimingFunction: 'ease-in-out',
            transitionDelay: 'slowest',
            animationName: {s: 'fade-in'},
            animationDuration: {s: 'fast'},
            _motionReduce: {
                transitionDuration: 'fastest',
                transitionDelay: '0ms',
                animationDuration: 'fastest'
            },
            bg: 'primary.base.white',
            flexDirection: 'row',
            gap: {base: 'spacing-16', s: 'spacing-24'}
        },
        searchEndElement: {
            pl: 'spacing-8',
            pr: 'spacing-16'
        },
        search: {
            width: {base: '6.625rem', s: 'full'},
            flexShrink: 1,
            transitionProperty: 'width',
            transitionDuration: 'slower',
            transitionTimingFunction: 'ease-in-out',
            _motionReduce: {
                transitionDuration: 'fastest'
            }
        },
        searchInput: {
            bg: 'background.section.default',
            textStyle: 'body.small',
            boxSizing: 'border-box',
            borderRadius: {base: 'full', s: 's'},
            // Published by `form`, the box this height is measured against.
            height: {base: '2.75rem', s: 'var(--search-overlay-input-block-size)'},
            transitionProperty: {base: 'height, border-radius', s: 'height'},
            transitionDuration: 'slower',
            transitionTimingFunction: 'ease-in-out',
            transitionDelay: {base: '0ms', s: 'slowest'},
            _motionReduce: {
                transitionDuration: 'fastest',
                transitionDelay: '0ms'
            },
            _placeholder: {
                textStyle: 'body.small',
                color: 'text.secondary'
            },
            // Safari zooms the page in when a focused input renders below 16px,
            // and the zoom outlives the overlay closing — any touch pointer
            // keeps the input at the 16px the rest of the app's inputs already
            // use. A paired trackpad makes an iPad the primary `fine` pointer
            // while its screen still zooms, so the query reads `any-pointer`.
            '@media (any-pointer: coarse)': {
                textStyle: 'body.medium',
                _placeholder: {
                    textStyle: 'body.medium'
                }
            }
        },
        contentContainer: {
            display: 'flex',
            pt: {base: 'spacing-24', s: 'spacing-20'},
            pb: {base: 'spacing-24', s: 'spacing-40'},
            border: {base: 'none', s: '1px solid'},
            borderColor: {s: 'border.subtle'},
            px: 'spacing-24',
            flexDirection: 'column',
            bg: 'primary.base.white',
            gap: {base: 'spacing-16', s: 'spacing-24'},
            flex: '1 1 auto',
            minBlockSize: 0,
            borderRadius: {base: 0, s: 'l'},
            mt: {base: 0, s: 'spacing-4'},
            overflowY: 'auto',
            scrollbarWidth: {s: 'none'},
            msOverflowStyle: {s: 'none'},
            '&::-webkit-scrollbar': {
                display: {s: 'none'}
            },
            animationName: 'slide-from-bottom, fade-in',
            animationDuration: 'slowest',
            // The design-specified easing is not currently represented by a theme token.
            animationTimingFunction: 'cubic-bezier(0, 0, 0, 1)'
        },
        contentItem: {
            py: 'spacing-12',
            pl: 'spacing-12',
            pr: 'spacing-16',
            display: 'flex',
            flexDirection: 'row',
            gap: 'spacing-8'
        },
        contentItemLabel: {
            color: 'text.primary',
            textStyle: 'body.small'
        },
        result: {
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            gap: 'spacing-12',
            cursor: 'pointer',
            py: 'spacing-12',
            pl: 'spacing-12',
            pr: 'spacing-16',
            textAlign: 'start',
            border: '1px solid transparent',
            borderRadius: 's',
            _hover: {
                backgroundColor: 'background.section.subtle'
            },
            _focusVisible: {
                outline: 'none',
                backgroundColor: 'background.section.subtle',
                borderColor: 'border.strong'
            }
        },
        resultImage: {
            width: 'spacing-80',
            aspectRatio: 1,
            objectFit: 'contain',
            borderRadius: 's',
            flexShrink: '0',
            backgroundColor: 'background.section.subtle'
        },
        resultText: {
            color: 'text.secondary'
        },
        contentLabel: {
            color: 'text.tertiary',
            textStyle: 'body.xsmall'
        },
        resultHighlight: {
            color: 'text.primary',
            backgroundColor: 'transparent',
            fontWeight: 'bold'
        },
        recentSearches: {
            display: 'flex',
            flexDirection: 'column',
            gap: 'spacing-8',
            alignItems: 'flex-start'
        },
        clearRecentSearchLink: {
            display: 'inline-flex',
            alignItems: 'center',
            width: 'fit-content',
            color: 'text.primary',
            textStyle: 'body.small',
            '& span': {
                borderBottomColor: 'text.primary'
            },
            _focusVisible: {
                outlineColor: 'text.primary'
            }
        },
        viewAllLink: {
            width: 'fit-content',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'spacing-8',
            color: 'text.brand-default',
            textStyle: 'body.small',
            '& span': {
                borderBottomColor: 'text.brand-default'
            },
            '& svg': {
                boxSize: 'spacing-20'
            }
        },
        searchIcon: {
            width: 'spacing-16',
            height: 'spacing-16'
        },
        noResultContainer: {
            display: 'flex',
            paddingBlock: 'spacing-12',
            gap: 'spacing-8',
            pl: 'spacing-12',
            pr: 'spacing-16'
        }
    },
    variants: {
        expanded: {
            true: {
                form: {
                    // The one term the expansion moves; the box follows it.
                    '--search-overlay-input-block-size': '3.25rem'
                },
                search: {
                    width: {base: 'full', s: 'full'}
                },
                searchInput: {
                    borderRadius: {base: 's', s: 's'}
                }
            },
            false: {}
        },
        closeVisible: {
            true: {
                closeButton: {
                    opacity: {base: 1, s: 1},
                    visibility: {base: 'visible', s: 'visible'}
                }
            },
            false: {}
        }
    },
    defaultVariants: {
        expanded: false,
        closeVisible: false
    }
})
